import { specialItemReferenceForTitle, GOD_OF_FISHING_TITLE_ID } from "../../map/map-special-item-references.js";
import { findQueryItemNameGroup } from "../query/item-name-groups.js";
import { fishingContestSelectionForQuery } from "../fishing-contest/contract.js";
import { tokenizeSearchExpressionDsl, searchExpressionTokenIsStandaloneOperator } from "../query/lex.js";
import { SearchExpressionBooleanParser } from "../query/boolean-parser.js";
import { buildQueryCatalogIndex, matchingQueryCatalogRecords, resolveQueryCatalogReference } from "../query/catalog-index.js";
import { createMissingTitlePredicate } from "./title-progress.js";

export const QUERY_STATUSES = Object.freeze(["caught", "missing", "favourite", "missing-titles"]);
export const QUERY_GRADES = Object.freeze(["red", "yellow", "blue", "green", "white"]);
export const QUERY_METHODS = Object.freeze(["rod", "hotspot", "harpoon"]);
const FIELDS = new Set(["fish", "title", "zone", "potential-zone", "status", "grade", "method"]);
class FishQueryParser extends SearchExpressionBooleanParser {
  constructor(source, index) {
    super(source);
    this.catalogIndex = index;
    this.terms = [];
  }
  resolveCatalogValue(value, field) {
    if (!this.catalogIndex) return { status: "catalog_unavailable" };
    if (/^\d+$/.test(value) && ["fish", "title"].includes(field)) {
      const record = matchingQueryRecords(this.catalogIndex, value, field)[0];
      return record ? { status: "resolved", record } : { status: "unknown" };
    }
    const group = ["text", "fish"].includes(field) && fishQueryNameGroup(this.catalogIndex, value);
    if (group) return { status: "resolved", record: group };
    const resolve = kind => resolveQueryCatalogReference([this.catalogIndex], value,
      record => record.kind === kind ? `${kind}:${record.id ?? record.entry.rgb}` : "");
    if (field !== "text") return resolve(field === "potential-zone" ? "zone" : field);
    const fish = resolve("fish");
    return fish.status === "unknown" ? resolve("title") : fish;
  }
  bind(node, start, end) { return { ...node, sourceStart: start, sourceEnd: end, sourceText: this.source.slice(start, end) }; }
  makeGroupNode(operator, children) {
    return this.bind({ kind: "group", operator, children }, children[0].sourceStart, children.at(-1).sourceEnd);
  }
  decorateParenthesizedNode(node, open, close) { return this.bind(node, open.start, close.end); }
  applyParsedNegation(node, start) { return this.bind({ ...node, negated: !node.negated }, start, node.sourceEnd); }
  handleUnterminatedGroup() { this.addIssue({ code: "unclosed_group", message: "Expected )." }); }
  parseTerm() {
    const token = this.parseValueToken();
    if (!token || token.closed === false) {
      this.addIssue({ code: "expected_value", message: "Expected a complete name or filter." });
      return null;
    }
    let field = "text", value = token.value, record;
    if (this.matchSymbol(":")) {
      field = value.toLowerCase();
      const next = this.parseValueToken();
      if (!FIELDS.has(field) || !next || next.closed === false) {
        this.addIssue({ code: "invalid_filter", message: "Expected a supported filter and a complete value." });
        return null;
      }
      value = next.value;
      if (["fish", "title", "zone", "potential-zone"].includes(field)) {
        const selected = this.resolveSpacedValueToken(next, value => this.resolveCatalogValue(value, field));
        value = selected.token.value;
        record = ["zone", "potential-zone"].includes(field) ? undefined : selected.resolved.record;
      }
    } else {
      const selected = this.resolveSpacedValueToken(token, value => this.resolveCatalogValue(value, "text"));
      value = selected.token.value;
      record = selected.resolved.record;
      const lower = value.toLowerCase();
      if (token.type === "word" && selected.token.end === token.end) {
        if (QUERY_STATUSES.includes(lower)) field = "status";
        else if (QUERY_GRADES.includes(lower)) field = "grade";
        else if (QUERY_METHODS.includes(lower)) field = "method";
        else if (lower === "dried" || lower === "contest") field = lower;
        if (field !== "text") record = undefined;
      }
    }
    if (["status", "grade", "method", "dried", "contest"].includes(field)) value = value.toLowerCase();
    const choices = { status: QUERY_STATUSES, grade: QUERY_GRADES, method: QUERY_METHODS }[field];
    if (!value || (choices && !choices.includes(value))) {
      this.addIssue({ code: "invalid_value", message: "Unknown filter value." });
      return null;
    }
    const node = this.bind({ kind: "term", field, value, ...(record ? { record } : {}) }, token.start, this.previousEnd());
    this.terms.push(node);
    return node;
  }
}
export function parseFishQuery(source, index) {
  if (typeof source !== "string" || [...source].length > 160) throw new TypeError("Invalid Fishydex query");
  if (!source.trim()) return { expression: null, issues: [], fields: new Set(), requiresTitles: false };
  const parser = new FishQueryParser(source, index);
  const expression = parser.parse();
  const fields = new Set();
  let requiresTitles = false;
  function visit(node) {
    if (node?.kind === "group") node.children.forEach(visit);
    else if (node) {
      fields.add(node.field);
      if (["title", "text"].includes(node.field) || (node.field === "status" && node.value === "missing-titles")) requiresTitles = true;
    }
  }
  visit(expression);
  return { expression, terms: parser.terms, issues: parser.issues, fields, requiresTitles, leadingExpressionEnd: parser.leadingExpressionEnd };
}
const itemId = entry => entry.item_id ?? entry.itemId;
export function fishQueryIndex(fish, titles, zones = []) {
  const records = [
    ...fish.map(entry => ({ kind: "fish", entry, id: itemId(entry), itemId: itemId(entry), itemName: entry.name, label: entry.name, ids: [itemId(entry)], values: [entry.name, String(itemId(entry))] })),
    ...titles.filter(title => title.name && (title.itemId !== null || title.titleId === GOD_OF_FISHING_TITLE_ID)).map(title => {
      const reference = title.itemId === null ? specialItemReferenceForTitle(title.titleId, fish) : null;
      const ids = title.itemId !== null ? [title.itemId] : reference?.kind === "item-group" ? reference.itemIds : reference ? [reference.itemId] : [];
      return { kind: "title", entry: title, id: title.titleId, label: title.name, ids, values: [title.name, String(title.titleId)] };
    }),
    ...zones.map(zone => ({ kind: "zone", entry: zone, label: zone.name ?? zone.rgb, ids: zone.known, potential: zone.potential, values: [zone.name, zone.rgb] })),
  ];
  return buildQueryCatalogIndex(records);
}
export function fishQueryNameGroup(index, value) {
  const group = findQueryItemNameGroup([index], value);
  return group ? { kind: "fish-group", label: group.name, ids: group.itemIds, members: group.records } : null;
}
export function matchingQueryRecords(index, value, field = "text") {
  if (["zone", "potential-zone"].includes(field) && /^\d{1,3},\d{1,3},\d{1,3}$/.test(value)) return index.records.filter(record => record.kind === "zone" && record.entry.rgb === value);
  if ((field === "fish" || field === "title") && /^\d+$/.test(value)) return index.records.filter(record => record.kind === field && record.id === Number(value));
  return matchingQueryCatalogRecords(index, value,
    record => field === "text" ? record.kind !== "zone" : record.kind === (field === "potential-zone" ? "zone" : field));
}
export function fishQueryTermRecords(node, index) {
  return node.record ? [node.record] : matchingQueryRecords(index, node.value, node.field);
}
export function compileFishQuery(parsed, { index, titles, progress, guideCount, caughtIds, favouriteIds, grade, contestSelection, nowSeconds }) {
  if (parsed.issues.some(issue => issue.severity === "error")) return () => false;
  const contest = parsed.fields.has("contest") ? fishingContestSelectionForQuery(contestSelection, nowSeconds) : null;
  if (parsed.fields.has("contest") && !contest) return () => false;
  const contestIds = new Set(contest?.itemIds);
  const missingTitles = createMissingTitlePredicate(titles, progress, guideCount);
  function compile(node) {
    if (!node) return () => true;
    let match;
    if (node.kind === "group") {
      const children = node.children.map(compile);
      match = node.operator === "and" ? entry => children.every(fn => fn(entry)) : entry => children.some(fn => fn(entry));
    } else if (["text", "fish", "title", "zone", "potential-zone"].includes(node.field)) {
      const ids = new Set(fishQueryTermRecords(node, index).flatMap(record => node.field === "potential-zone" ? record.potential : record.ids));
      match = entry => ids.has(itemId(entry));
    } else if (node.field === "grade") match = entry => grade(entry) === node.value;
    else if (node.field === "method") match = entry => entry.catch_methods.includes(node.value);
    else if (node.field === "contest") match = entry => contestIds.has(itemId(entry));
    else if (node.field === "dried") match = entry => entry.is_dried;
    else if (node.value === "missing-titles") match = missingTitles;
    else if (node.value === "favourite") match = entry => favouriteIds.has(itemId(entry));
    else match = entry => caughtIds.has(itemId(entry)) === (node.value === "caught");
    return node.negated ? entry => !match(entry) : match;
  }
  return compile(parsed.expression);
}

// Completion replaces the resolved phrase at the caret; incomplete values keep lexical spans.
export function fishQueryCompletion(source, caret = source.length, index) {
  const term = index && parseFishQuery(source, index).terms?.find(term => term.sourceStart <= caret && term.sourceEnd >= caret);
  if (term?.record) return { field: term.field, value: term.value, start: term.sourceStart, end: term.sourceEnd };
  const tokens = tokenizeSearchExpressionDsl(source);
  let position = tokens.findIndex(token => token.start <= caret && token.end >= caret);
  if (position < 0) return { field: "text", value: "", start: caret, end: caret };
  let token = tokens[position];
  if (searchExpressionTokenIsStandaloneOperator(token) || ["(", ")"].includes(token.value)) {
    return { field: "text", value: "", start: caret, end: caret };
  }
  let field = "text", start = token.start, end = token.end;
  if (token.value === ":" && tokens[position - 1]) {
    field = tokens[position - 1].value.toLowerCase(); start = tokens[position - 1].start;
    return { field, value: "", start, end };
  }
  if (tokens[position - 1]?.value === ":" && tokens[position - 2]) {
    field = tokens[position - 2].value.toLowerCase(); start = tokens[position - 2].start;
  }
  return { field, value: token.value, start, end };
}

export function applicableFishQuery(source, index) {
  const parsed = parseFishQuery(source, index);
  if (!parsed.issues.some(issue => issue.severity === "error")) return { parsed, source, state: source.trim() ? "applied" : "empty" };
  if (parsed.leadingExpressionEnd > 0) {
    const prefix = source.slice(0, parsed.leadingExpressionEnd);
    return { parsed: parseFishQuery(prefix, index), source: prefix, state: "partial" };
  }
  return { parsed, source: "", state: "invalid" };
}
